import { useRef } from "react";
import { motion } from "framer-motion";
import { GripVertical, X } from "lucide-react";

// A single piece of Game Show room decor/functionality (the Wheel, the Trivia Board, or the
// Door) positioned on the canvas by percentage. Centering uses a plain CSS transform on the
// OUTER wrapper (untouched by framer-motion); the INNER motion.div owns drag exclusively via its
// own x/y motion values, so the two transforms never fight. After a drag commits a new
// percentage position, the parent remounts us via `key` so framer's internal offset resets to 0
// instead of double-applying on top of the new base position.
export function GameShowStageFrame({ elementKey, canvasRef, pos, editMode, onMoved, onDelete, label, icon: Icon, className, children }) {
  const dragRef = useRef(null);

  const handleDragEnd = (_e, info) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const dxPct = (info.offset.x / rect.width) * 100;
    const dyPct = (info.offset.y / rect.height) * 100;
    onMoved(pos.x + dxPct, pos.y + dyPct);
  };

  return (
    <div
      className="absolute z-20"
      style={{ left: `${pos.x}%`, top: `${pos.y}%`, transform: "translate(-50%, -50%)" }}
      data-testid={`gameshow-stage-position-${elementKey}`}
    >
      <motion.div
        ref={dragRef}
        drag={editMode}
        dragMomentum={false}
        dragElastic={0.05}
        onDragEnd={handleDragEnd}
        whileDrag={{ scale: 1.04 }}
        whileHover={editMode ? { scale: 1.02 } : {}}
        className={`relative ${editMode ? "cursor-grab active:cursor-grabbing" : ""} ${className || ""}`}
      >
        <div className={`relative rounded-2xl backdrop-blur-md bg-black/55 shadow-2xl p-3 transition-colors ${editMode ? "border-2 border-dashed border-[#D4AF37]/70" : "border border-white/10"}`}>
          {editMode && (
            <>
              <div className="absolute -top-2.5 -left-2.5 w-6 h-6 rounded-full bg-[#D4AF37] flex items-center justify-center text-black shadow-lg" title="Drag to reposition">
                <GripVertical className="w-3.5 h-3.5" />
              </div>
              <button
                data-testid={`gameshow-delete-element-${elementKey}`}
                onClick={onDelete}
                title="Remove from the room this match"
                className="absolute -top-2.5 -right-2.5 w-6 h-6 rounded-full bg-[#D90429] hover:bg-[#b8031f] flex items-center justify-center text-white shadow-lg"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </>
          )}
          {label && (
            <p className="text-[10px] uppercase tracking-widest text-[#D4AF37]/80 mb-1.5 flex items-center gap-1.5 font-display">
              {Icon && <Icon className="w-3 h-3" />} {label}
            </p>
          )}
          {children}
        </div>
      </motion.div>
    </div>
  );
}
